import { CssBaseline, Stack, ThemeProvider } from "@mui/material";
import { Title } from "@src/appV2/redesign/system/ui-components";
import { getTheme } from "@src/appV2/redesign/system/ui-theme";
import { useRef } from "react";
import { Virtuoso } from "react-virtuoso";

import { APP_V2_USER_EVENTS } from "../lib";
import { logEvent } from "../lib/analytics";
import { useLogEffect } from "../lib/analytics/useLogEffect";
import { PageWrapper } from "../redesign/components/PageWrapper";
import { Navbar } from "../redesign/Navbar";
import { PullToRefresh } from "../redesign/ShiftDiscovery/ListView/PullToRefresh";
import { useScrollToTopOnStatusTap } from "../redesign/useScrollToTopOnStatusTap";
import { getSafeAreaPaddings } from "../redesign/utils/getSafeAreaPaddings";
import { extractIncludedEntitiesFromNewsFeedItems } from "./api/extractIncludedEntitiesFromBookingRequests";
import { useGetNewsFeedItems } from "./api/useGetNewsFeedItems";
import NewsFeedItemContentCard from "./ContentCard";
import { NewsFeedEmptyPageState } from "./EmptyPageState";
import { NewsFeedErrorPageState } from "./ErrorPageState";
import { NewsFeedLoadingMoreState } from "./LoadingMoreState";
import { NewsFeedLoadingPageState } from "./LoadingPageState";
import { NewsFeedReachedEndState } from "./ReachedEndState";

export function NewsFeedPage() {
  const {
    data,
    isLoading,
    isError,
    isSuccess,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  } = useGetNewsFeedItems(
    { include: "worker,workplace,shift" },
    { useErrorBoundary: false, staleTime: 60 * 1000 }
  );

  const newsFeedItems = data?.pages?.flatMap((page) => page.data) ?? [];
  const newsFeedIncluded = data?.pages?.flatMap((page) => page.included ?? []);

  const stackRef = useRef<HTMLDivElement>(null);
  useScrollToTopOnStatusTap(stackRef);

  useLogEffect(APP_V2_USER_EVENTS.NEWS_FEED_PAGE_VIEWED);

  return (
    <ThemeProvider theme={getTheme()}>
      <CssBaseline />
      <PageWrapper
        sx={{
          ...getSafeAreaPaddings({
            top: true,
          }),
          position: "relative",
        }}
      >
        <PullToRefresh scrollRef={stackRef} onRefresh={refetch} />
        <Stack ref={stackRef} py={5} px={4} spacing={3} overflow="auto" height="100%">
          <Title semibold component="h4">
            News Feed
          </Title>

          {isLoading && <NewsFeedLoadingPageState />}
          {isError && <NewsFeedErrorPageState />}
          {isSuccess && newsFeedItems.length === 0 && <NewsFeedEmptyPageState />}
          {isSuccess && newsFeedItems.length > 0 && (
            <Stack height="100%">
              <Virtuoso
                data={newsFeedItems}
                customScrollParent={stackRef.current ?? undefined}
                width="100%"
                endReached={() => {
                  logEvent(APP_V2_USER_EVENTS.NEWS_FEED_BOTTOM_REACHED, {
                    hasNextPage,
                    fetchedPagesCount: data.pages.length,
                    currentItemsCount: newsFeedItems?.length,
                  });

                  if (hasNextPage) {
                    void fetchNextPage();
                  }
                }}
                components={{
                  EmptyPlaceholder: () => <NewsFeedEmptyPageState />,
                  Footer: () => (
                    <Stack py={5}>
                      {isFetchingNextPage && <NewsFeedLoadingMoreState />}
                      {(newsFeedItems?.length ?? 0) > 0 && !hasNextPage && (
                        <NewsFeedReachedEndState />
                      )}
                    </Stack>
                  ),
                }}
                itemContent={(_, item) => (
                  <Stack py={2}>
                    <NewsFeedItemContentCard
                      key={item.id}
                      newsFeedItem={item}
                      included={extractIncludedEntitiesFromNewsFeedItems(item, newsFeedIncluded)}
                    />
                  </Stack>
                )}
              />
            </Stack>
          )}
        </Stack>
        <Navbar />
      </PageWrapper>
    </ThemeProvider>
  );
}
